import { useState, useEffect, useRef } from "react";
import { Box, Grid, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useIsMobile } from "@/hooks/use-responsive";
import { useTheme } from "@/contexts/theme";

const ChevronIcon = ({ up }: { up?: boolean }) => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={up ? { transform: "rotate(180deg)" } : undefined}
    aria-hidden
  >
    <path d="m6 9 6 6 6-6" />
  </svg>
);

interface IFilterItem {
  id: string;
  label: string;
  component: React.ReactNode;
  width?: "xs" | "sm" | "md" | "lg" | "xl";
}

interface IEnhancedFilterSectionProps {
  filters: IFilterItem[];
  hasActiveFilters?: boolean;
  actionButtons?: React.ReactNode;
  tableActionButtons?: React.ReactNode;
  title?: string;
}

const FilterSectionComponent = ({
  filters,
  actionButtons,
  tableActionButtons,
}: IEnhancedFilterSectionProps) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const isModern = useTheme().currentTheme === "modern";

  // Check URL parameters on initial mount to determine if filters are active
  const hasUrlFilters = () => {
    if (typeof window === "undefined") return false;
    const urlParams = new URLSearchParams(window.location.search);
    const excludedParams = ["pageNumber", "pageSize", "page", "limit", "view"];

    // Convert to array and check if any non-excluded params exist
    const params = Array.from(urlParams.keys());
    return params.some((key) => !excludedParams.includes(key));
  };

  const [showAll, setShowAll] = useState(hasUrlFilters());
  const isInitialMount = useRef(true);

  // Only expand automatically on initial mount if there are filters in URL
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      if (hasUrlFilters()) {
        setShowAll(true);
      }
    }
  }, []);

  const renderFilterGrid = () => {
    const maxVisibleFilters = isMobile ? 1 : 5;
    const visibleFilters = showAll
      ? filters
      : filters.slice(0, maxVisibleFilters);
    const hasMoreFilters = filters.length > maxVisibleFilters;

    // Map width prop to grid sizes
    const getGridSize = (width?: string) => {
      switch (width) {
        case "xs":
          return 1.5;
        case "sm":
          return 2;
        case "md":
          return 2.5;
        case "lg":
          return 3;
        case "xl":
          return 4;
        default:
          return 1.5;
      }
    };

    return (
      <Box
        className="gm-filter-bar"
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "flex-end",
          gap: 0.5,
          marginTop: "2px",
          width: "100%",
        }}
      >
        {/* Filters + Search/Clear — takes remaining space, wraps instead of overlapping icons */}
        <Box sx={{ flex: "1 1 0", minWidth: 0 }}>
          <Grid container spacing={0.5}>
            {visibleFilters.map((filter) => (
              <Grid
                item
                xs={12}
                sm={4}
                md={2}
                lg={getGridSize(filter.width)}
                key={filter.id}
              >
                <Box
                  style={{
                    padding: 6,
                    height: "100%",
                  }}
                >
                  <Typography
                    className="gm-filter-label"
                    variant="body2"
                    title={filter.label}
                    style={{
                      marginBottom: 6,
                      fontWeight: 500,
                      color: "#374151",
                      fontSize: 13,
                      height: 20,
                      lineHeight: "20px",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {filter.label}
                  </Typography>
                  {filter.component}
                </Box>
              </Grid>
            ))}

            {/* Action buttons need more room than a filter field (Search + Clear + show more) */}
            {actionButtons && (
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <Box
                  style={{
                    padding: 6,
                    height: "100%",
                  }}
                >
                  {/* Empty label space to align with other filters */}
                  <Typography
                    className="gm-filter-label"
                    variant="body2"
                    style={{
                      marginBottom: 4,
                      fontWeight: 500,
                      color: "transparent",
                      fontSize: 13,
                      height: "20px",
                    }}
                  >
                    &nbsp;
                  </Typography>
                  <Box
                    className="gm-filter-actions"
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      alignItems: "center",
                      gap: 6,
                      maxWidth: "100%",
                    }}
                  >
                    {actionButtons}
                    {hasMoreFilters && isModern && (
                      <button
                        type="button"
                        className="gm-filter-more"
                        onClick={() => setShowAll(!showAll)}
                      >
                        <span>
                          {showAll
                            ? t("sales.quote.showLess")
                            : t("sales.quote.showMore")}
                        </span>
                        {!showAll && (
                          <span className="gm-filter-more-count">
                            {filters.length - maxVisibleFilters}
                          </span>
                        )}
                        <ChevronIcon up={showAll} />
                      </button>
                    )}
                    {hasMoreFilters && !isModern && (
                      <Box
                        onClick={() => setShowAll(!showAll)}
                        style={{
                          padding: "8px 12px",
                          backgroundColor: "transparent",
                          borderRadius: 6,
                          cursor: "pointer",
                          fontSize: 12,
                          color: "#374151",
                          fontWeight: 500,
                          transition: "all 0.2s ease",
                          display: "flex",
                          alignItems: "center",
                          gap: 4,
                          minHeight: "32px",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {showAll ? (
                          <>
                            <span>{t("sales.quote.showLess")}</span>
                            <span style={{ fontSize: 10 }}>▲</span>
                          </>
                        ) : (
                          <>
                            <span>{t("sales.quote.showMore")}</span>
                            <span
                              style={{
                                fontSize: 10,
                                backgroundColor: "#d1d5db",
                                color: "#6b7280",
                                padding: "1px 4px",
                                borderRadius: 3,
                                fontWeight: 500,
                              }}
                            >
                              +{filters.length - maxVisibleFilters}
                            </span>
                            <span style={{ fontSize: 10 }}>▼</span>
                          </>
                        )}
                      </Box>
                    )}
                  </Box>
                </Box>
              </Grid>
            )}
          </Grid>
        </Box>

        {/* View / Download — reserved space so filters never cross over */}
        {tableActionButtons && (
          <Box
            sx={{
              flex: "0 0 auto",
              display: "flex",
              alignItems: "flex-end",
              justifyContent: "flex-end",
              padding: "6px",
              alignSelf: "flex-end",
            }}
          >
            <Box
              className="gm-filter-table-actions"
              style={{
                display: "flex",
                gap: 6,
                flexDirection: "row",
              }}
            >
              {tableActionButtons}
            </Box>
          </Box>
        )}
      </Box>
    );
  };

  return <Box>{renderFilterGrid()}</Box>;
};

export { FilterSectionComponent };
export type { IFilterItem, IEnhancedFilterSectionProps };
